import {ApiClientError} from '@/api/client';
import {buildAddressHost, humaniseAddressError, normaliseAddress, preferFieldDetail} from '@/lib/publication-address';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useRenameSiteSlug} from '@/hooks/useRenameSiteSlug';
import {useSiteSlugCheck} from '@/hooks/useSiteSlugCheck';
import type {SlugRenameResult} from '@/types/api';

const MIN_LENGTH = 3;

const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)'
};

const dialogStyle: React.CSSProperties = {
    background: 'var(--tt-card-bg)',
    borderRadius: 14,
    padding: 32,
    width: 520,
    maxWidth: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid var(--tt-card-border)'
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 48,
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 400,
    borderRadius: 8,
    letterSpacing: '-0.013em',
    border: '1px solid var(--tt-input-border)',
    color: 'var(--tt-text-primary)',
    background: 'var(--tt-input-bg)',
    outline: 'none',
    transition: 'border-color .15s linear, box-shadow .15s linear',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    margin: '0 0 .4em',
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--tt-text-primary)'
};

const noteStyle: React.CSSProperties = {
    fontSize: '12px',
    lineHeight: 1.6,
    color: 'var(--tt-text-secondary)'
};

function handleInputFocus(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-accent)';
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
}

function handleInputBlur(e: React.FocusEvent<HTMLInputElement>) {
    e.currentTarget.style.borderColor = 'var(--tt-input-border)';
    e.currentTarget.style.boxShadow = 'none';
}

/** Everything that changes the moment the owner confirms, in plain language. */
const CONSEQUENCES = [
    'Your publication moves to the new address straight away.',
    'Links you have already shared keep working for 90 days, then stop.',
    'Anyone signed in on the old address has to sign in again.',
    'Your old address is held for you, so nobody else can take it.',
    'A custom domain of your own is not affected.',
    'You can change the address again in 30 days.'
];

interface ChangeAddressModalProps {
    siteId: string;
    siteName: string;
    currentSlug: string;
    siteUrl: string;
    onCancel: () => void;
    onRenamed: (result: SlugRenameResult) => void;
}

export function ChangeAddressModal({siteId, siteName, currentSlug, siteUrl, onCancel, onRenamed}: ChangeAddressModalProps) {
    const [slug, setSlug] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [error, setError] = useState('');
    const headingRef = useRef<HTMLHeadingElement>(null);

    const slugCheck = useSiteSlugCheck(siteId, slug);
    const rename = useRenameSiteSlug(siteId);

    useEffect(() => {
        headingRef.current?.focus();
    }, []);

    // A rename is irreversible for 30 days and nothing here can abort the request
    // once it is in flight, so every dismissal path is closed while it is pending —
    // not just the Cancel button.
    const renamePending = rename.isLoading;
    const requestClose = useCallback(() => {
        if (renamePending) {
            return;
        }
        onCancel();
    }, [renamePending, onCancel]);

    useEffect(() => {
        function onKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                requestClose();
            }
        }
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [requestClose]);

    const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSlug(normaliseAddress(e.target.value));
        setConfirmation('');
        setError('');
    }, []);

    const longEnough = slug.length >= MIN_LENGTH;
    const isSameAsCurrent = slug === currentSlug;
    const isChecking = longEnough && !isSameAsCurrent && (slugCheck.isLoading || slugCheck.isFetching);
    const isAvailable = !isSameAsCurrent && slugCheck.data?.available === true && slugCheck.data.slug === slug && !isChecking;
    const isTaken = !isSameAsCurrent && slugCheck.data?.available === false && slugCheck.data.slug === slug && !isChecking;
    const checkFailed = longEnough && !isSameAsCurrent && slugCheck.isError && !isChecking;
    const confirmed = confirmation.trim().toLowerCase() === slug && slug.length > 0;
    const canSubmit = isAvailable && confirmed && !renamePending;

    const newHost = longEnough ? buildAddressHost(siteUrl, slug) : '';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!canSubmit) {
            return;
        }

        try {
            const result = await rename.mutateAsync(slug);
            onRenamed(result);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(humaniseAddressError(preferFieldDetail(err.message, err.details)));
            } else {
                setError('Something went wrong and the address was not changed. Please try again.');
            }
        }
    };

    // A failed check leaves the owner with no confirmation field and a dead button,
    // so it has to say why. The server rejects a trailing hyphen with a 400, which
    // the input deliberately lets you type.
    let checkFailureMessage = '';
    if (checkFailed) {
        const checkError = slugCheck.error;
        checkFailureMessage = checkError instanceof ApiClientError && checkError.status === 400
            ? preferFieldDetail(checkError.message, checkError.details)
            : 'We could not check that address just now. Change it or try again in a moment.';
    }

    let addressHint = `This is the part before ${buildAddressHost(siteUrl, '').replace(/^\./, '')}.`;
    if (isSameAsCurrent) {
        addressHint = 'That is already your address. Pick a different one.';
    } else if (checkFailed) {
        addressHint = checkFailureMessage;
    } else if (isTaken) {
        addressHint = slugCheck.data?.error || 'That address is not available. Try another one.';
    } else if (longEnough && newHost) {
        addressHint = newHost;
    }

    return (
        <div style={overlayStyle} onClick={requestClose}>
            <div aria-labelledby="change-address-title" aria-modal="true" className="studio-panel" role="dialog"
                style={dialogStyle}
                onClick={e => e.stopPropagation()}
            >
                <h3 ref={headingRef} id="change-address-title" style={{fontSize: '1.8rem', fontWeight: 500, color: 'var(--tt-text-primary)', margin: '0 0 8px', lineHeight: 1.4, letterSpacing: '-.025em', outline: 'none'}}
                    tabIndex={-1}
                >
                    Change publication address
                </h3>
                <p style={{...noteStyle, fontSize: '1.4rem', margin: '0 0 24px'}}>
                    <strong style={{color: 'var(--tt-text-primary)'}}>{siteName}</strong> is published at{' '}
                    <strong style={{color: 'var(--tt-text-primary)'}}>{buildAddressHost(siteUrl, currentSlug)}</strong>.
                </p>

                <form autoComplete="off" onSubmit={handleSubmit}>
                    <div style={{marginBottom: 24}}>
                        <label htmlFor="publication-address" style={labelStyle}>New address</label>
                        <div style={{position: 'relative'}}>
                            <input autoComplete="off"
                                className="studio-field-control"
                                id="publication-address"
                                placeholder="the-daily-awesome"
                                style={{...inputStyle, paddingRight: 44}}
                                type="text"
                                value={slug}
                                autoFocus
                                onBlur={handleInputBlur}
                                onChange={handleSlugChange}
                                onFocus={handleInputFocus}
                            />
                            {longEnough && !isSameAsCurrent && (
                                <div style={{position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center'}}>
                                    {isChecking && (
                                        <svg aria-label="Checking availability" height="18" role="img" style={{animation: 'spin 1s linear infinite'}} viewBox="0 0 16 16" width="18">
                                            <circle cx="8" cy="8" fill="none" r="6" stroke="#CED4D9" strokeDasharray="28" strokeDashoffset="8" strokeLinecap="round" strokeWidth="2"/>
                                        </svg>
                                    )}
                                    {isAvailable && (
                                        <svg aria-label="Address is available" fill="none" height="20" role="img" viewBox="0 0 24 24" width="20">
                                            <path d="M20 6L9 17L4 12" stroke="var(--studio-accent)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"/>
                                        </svg>
                                    )}
                                    {isTaken && (
                                        <svg aria-label="Address is not available" fill="none" height="18" role="img" viewBox="0 0 24 24" width="18">
                                            <path d="M18 6L6 18M6 6L18 18" stroke="#F50B23" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"/>
                                        </svg>
                                    )}
                                </div>
                            )}
                        </div>
                        <p role={checkFailed ? 'alert' : undefined}
                            style={{...noteStyle, margin: '8px 0 0', color: isTaken || isSameAsCurrent || checkFailed ? 'var(--tt-red)' : 'var(--tt-text-secondary)'}}
                        >
                            {addressHint}
                            {checkFailed && (
                                <button
                                    style={{marginLeft: 8, padding: 0, border: 0, background: 'none', color: 'var(--tt-accent)', font: 'inherit', textDecoration: 'underline', cursor: 'pointer'}}
                                    type="button"
                                    onClick={() => void slugCheck.refetch()}
                                >
                                    Try again
                                </button>
                            )}
                        </p>
                    </div>

                    <div style={{padding: 18, borderRadius: 10, background: 'var(--tt-input-bg)', border: '1px solid var(--tt-card-border)', marginBottom: 24}}>
                        <p style={{...labelStyle, margin: '0 0 10px'}}>What happens when you change it</p>
                        <ul style={{margin: 0, paddingLeft: 18, display: 'grid', gap: 7}}>
                            {CONSEQUENCES.map(line => <li key={line} style={noteStyle}>{line}</li>)}
                        </ul>
                    </div>

                    {isAvailable && (
                        <div style={{marginBottom: 24}}>
                            <label htmlFor="publication-address-confirm" style={labelStyle}>
                                Type <strong style={{fontWeight: 600}}>{slug}</strong> to confirm
                            </label>
                            <input autoComplete="off"
                                className="studio-field-control"
                                id="publication-address-confirm"
                                placeholder={slug}
                                style={inputStyle}
                                type="text"
                                value={confirmation}
                                onBlur={handleInputBlur}
                                onChange={e => setConfirmation(e.target.value)}
                                onFocus={handleInputFocus}
                            />
                        </div>
                    )}

                    {error && (
                        <p role="alert" style={{margin: '0 0 20px', color: 'var(--tt-red)', fontSize: '13px', lineHeight: 1.5}}>{error}</p>
                    )}

                    <div style={{display: 'flex', justifyContent: 'flex-end', gap: 12}}>
                        <button className="studio-control" disabled={renamePending}
                            style={{height: 40, padding: '0 20px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: renamePending ? 'not-allowed' : 'pointer', fontFamily: 'inherit'}}
                            type="button"
                            onClick={requestClose}
                        >
                            Cancel
                        </button>
                        <button className="studio-control studio-control-primary" disabled={!canSubmit}
                            style={{height: 40, padding: '0 20px', borderRadius: 8, fontSize: '13px', fontWeight: 500, cursor: canSubmit ? 'pointer' : 'not-allowed', fontFamily: 'inherit'}}
                            type="submit"
                        >
                            {renamePending ? 'Changing address…' : 'Change address'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
